import { Bot, Database, Brain, Lock, Cpu, Puzzle, Zap, Shield, Sparkles, Globe, BarChart3, Layers, MessageCircle } from 'lucide-react'
import { useScrollAnim } from '../../hooks/use-scroll-anim'
import { useI18n } from '../../i18n'
import { useState, useEffect, useRef } from 'react'

// Card 0: AI管家 demos
const butlerDemos = [
  {
    zh: { user: '我们门店新来的服务员问，招牌酸菜鱼的鱼片用量标准是多少？', bot: '根据《菜品标准手册 v3.2》第7章规定：\n\n招牌酸菜鱼（标准份）鱼片用量为 350g±10g，需使用黑鱼片，厚度0.5cm。腌制配料：盐3g、料酒10ml、蛋清半个、淀粉15g。具体操作步骤请参考手册7.3节。', source: '来源：菜品标准手册 v3.2 / 更新于 2025-03-15' },
    en: { user: 'A new waiter asks — what\'s the fish slice portion for our signature dish?', bot: 'Per Dish Standards Manual v3.2, Ch.7:\n\nStandard portion: 350g±10g, black fish, 0.5cm thick. Marinade: salt 3g, cooking wine 10ml, half egg white, starch 15g. See manual 7.3 for details.', source: 'Source: Dish Standards Manual v3.2 / Updated 2025-03-15' },
  },
  {
    zh: { user: '新员工入职需要走哪些流程？需要准备什么材料？', bot: '根据《员工入职管理规范》第二条：\n\n入职流程共5步：\n1. HR发放Offer → 签署劳动合同\n2. IT开通邮箱、工号、门禁卡\n3. 行政分配工位及办公用品\n4. 部门导师安排入职培训（3天）\n5. 试用期满30天进行首次考核\n\n需准备：身份证原件、学历证明、银行卡、体检报告、1寸照片2张。', source: '来源：员工入职管理规范 / 更新于 2025-02-20' },
    en: { user: 'What\'s the onboarding process? What documents are needed?', bot: 'Per Onboarding Policy Article 2:\n\n5-step onboarding:\n1. HR sends Offer → Sign employment contract\n2. IT sets up email, ID, access card\n3. Admin assigns desk & supplies\n4. Mentor arranges 3-day training\n5. 30-day probation review\n\nRequired: ID, degree certificate, bank card, health check, 2 photos.', source: 'Source: Onboarding Policy / Updated 2025-02-20' },
  },
  {
    zh: { user: '本月门店巡检有哪些不合格项？需要限期整改的是哪些？', bot: '根据3月巡检报告汇总：\n\n不合格项共4项：\n1. 🔴 朝阳店-冷库温度超标（-12℃，标准-18℃）→ 限期3天\n2. 🟡 海淀店-员工未佩戴工牌 → 限期7天\n3. 🟡 西直门店-消防通道堆放杂物 → 限期5天\n4. 🔴 望京店-食品安全台账未更新 → 限期3天\n\n红色项目需店长签字确认整改方案，5日内回访复查。', source: '来源：3月门店巡检报告 / 更新于 2025-03-28' },
    en: { user: 'What failed this month\'s store inspections? Which need immediate action?', bot: 'March inspection summary:\n\n4 failed items:\n1. 🔴 Chaoyang - Freezer temp too high (-12°C, std -18°C) → 3 days\n2. 🟡 Haidian - Staff not wearing badges → 7 days\n3. 🟡 Xizhimen - Fire exit blocked → 5 days\n4. 🔴 Wangjing - Food safety log not updated → 3 days\n\nRed items require manager-signed action plan, re-inspection within 5 days.', source: 'Source: March Store Inspection Report / Updated 2025-03-28' },
  },
  {
    zh: { user: '公司今年的差旅报销标准有变化吗？出差住宿上限是多少？', bot: '根据2025年新版《差旅费用管理办法》：\n\n与2024年对比主要变化：\n- 一线城市住宿上限：800元/晚 → 900元/晚 ↑\n- 二线城市住宿上限：500元/晚 → 550元/晚 ↑\n- 高铁出行标准不变：二等座\n- 新增：飞机出行需提前7天预订，否则不予报销\n\n注意：超标准部分需部门VP审批。', source: '来源：差旅费用管理办法 2025版 / 更新于 2025-01-10' },
    en: { user: 'Has the travel reimbursement policy changed this year? What\'s the hotel limit?', bot: 'Per 2025 Travel Expense Policy:\n\nKey changes vs 2024:\n- Tier-1 city hotel cap: ¥800/night → ¥900/night ↑\n- Tier-2 city hotel cap: ¥500/night → ¥550/night ↑\n- High-speed rail: no change (2nd class)\n- NEW: Flights must be booked 7 days ahead or no reimbursement\n\nNote: Overspending requires VP approval.', source: 'Source: Travel Expense Policy 2025 / Updated 2025-01-10' },
  },
]

// Card 1: 知识库 demos
const kbDemos = [
  {
    zh: { user: '客户投诉说我们的退换货流程太慢了，具体流程是什么？能加急吗？', bot: '根据《售后服务管理办法》第四章：\n\n退换货标准流程（3个工作日）：\n1. 客户提交退换申请 → 客服1h内确认\n2. 物流上门取件（1-2天）\n3. 仓库验收入库（当天）\n4. 财务审核退款（1天）\n\n加急通道：金额<500元可走"极速退款"，客服确认后2h内原路退回，无需等退货入库。', source: '来源：售后服务管理办法 / 更新于 2025-04-01' },
    en: { user: 'Customer says returns are too slow. What\'s the process? Can it be expedited?', bot: 'Per After-Sales Policy Ch.4:\n\nStandard process (3 business days):\n1. Customer submits → CS confirms within 1h\n2. Pickup (1-2 days)\n3. Warehouse inspection (same day)\n4. Finance refund (1 day)\n\nExpedited: <¥500 qualifies for "Instant Refund" — refunded within 2h, no warehouse wait.', source: 'Source: After-Sales Policy / Updated 2025-04-01' },
  },
  {
    zh: { user: '帮我查一下设备DZ-2200的安全操作规程', bot: '已检索到《设备安全操作规程汇编》中DZ-2200相关内容：\n\nDZ-2200数控机床安全操作规程：\n- 开机前：检查润滑系统油位≥2/3，确认防护罩完好\n- 运行中：主轴转速不得超过8000rpm，切削液流量≥15L/min\n- 停机：先降速至0再断电，严禁急停按钮用于正常停机\n- 维保：每500小时更换主轴轴承润滑脂\n\n⚠️ 安全警告：操作时必须佩戴护目镜，严禁戴手套操作旋转部件。', source: '来源：设备安全操作规程汇编 / 更新于 2025-03-01' },
    en: { user: 'Look up the safety operating procedure for equipment DZ-2200', bot: 'Found in Equipment Safety Procedures Manual:\n\nDZ-2200 CNC Safety SOP:\n- Before start: Check lube oil ≥2/3, confirm guard intact\n- During operation: Spindle ≤8000rpm, coolant ≥15L/min\n- Shutdown: Ramp to 0 then power off, no e-stop for normal shutdown\n- Maintenance: Replace spindle bearing grease every 500h\n\n⚠️ Must wear safety goggles. No gloves near rotating parts.', source: 'Source: Equipment Safety Procedures Manual / Updated 2025-03-01' },
  },
  {
    zh: { user: '合规审计发现我们产品宣传页有三处表述需要修改，具体是哪三处？', bot: '根据2025年Q1合规审计报告：\n\n需修改的3处宣传表述：\n1. "100%治愈率" → 建议改为"有效率98.5%"（违反《广告法》绝对化用语禁令）\n2. "行业第一品牌" → 建议改为"行业领先品牌之一"（无法提供权威排名依据）\n3. "无任何副作用" → 建议改为"不良反应发生率<0.1%"（与临床数据不符）\n\n整改期限：4月15日前完成修改并提交合规部复核。', source: '来源：2025 Q1合规审计报告 / 更新于 2025-04-05' },
    en: { user: 'The compliance audit flagged 3 product page claims for revision. Which ones?', bot: 'Per 2025 Q1 Compliance Audit Report:\n\n3 claims requiring revision:\n1. "100% cure rate" → Suggest "98.5% effective rate" (violates Advertising Law absolute claims ban)\n2. "#1 brand in industry" → Suggest "One of the industry-leading brands" (no authoritative ranking evidence)\n3. "Zero side effects" → Suggest "Adverse reaction rate <0.1%" (inconsistent with clinical data)\n\nDeadline: April 15 for revision and compliance review.', source: 'Source: 2025 Q1 Compliance Audit Report / Updated 2025-04-05' },
  },
]

const allDemos = [butlerDemos, kbDemos]

function ChatOverlay({ demoIdx, iconBg, icon: Icon, title, isHovered }: {
  demoIdx: number, iconBg: string, icon: any, title: string, isHovered: boolean
}) {
  const { lang, t } = useI18n()
  const demos = allDemos[demoIdx]
  const [chatIdx, setChatIdx] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (isHovered) {
      intervalRef.current = setInterval(() => {
        setChatIdx((prev) => (prev + 1) % demos.length)
      }, 4000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [isHovered, demos.length])

  const demo = demos[chatIdx]
  const text = lang === 'zh' ? demo.zh : demo.en

  return (
    <div className={`absolute inset-0 rounded-3xl bg-white/95 backdrop-blur-sm p-6 flex flex-col overflow-hidden transition-all duration-500 ${
      isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97] pointer-events-none'
    }`}>
      <div className="flex items-center gap-2 mb-3 shrink-0">
        <div className={`h-6 w-6 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className="h-3 w-3 text-white" />
        </div>
        <span className="text-xs font-semibold text-[#1a1a1a]">{title}</span>
        <span className="ml-auto text-[10px] text-gray-300">{t('实际对话记录', 'Live Chat')}</span>
      </div>
      {/* Dot indicators */}
      <div className="flex gap-1 mb-3 shrink-0">
        {demos.map((_, i) => (
          <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === chatIdx ? 'w-4 bg-violet-400' : 'w-1.5 bg-gray-200'}`} />
        ))}
      </div>
      {/* Chat area with scroll */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* User message */}
        <div className="flex justify-end mb-2">
          <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-tr-md bg-[#1a1a1a] text-white text-[12px] leading-relaxed">
            {text.user}
          </div>
        </div>
        {/* Bot message */}
        <div className="flex justify-start mb-2">
          <div className="max-w-[92%] px-3.5 py-2.5 rounded-2xl rounded-tl-md bg-gray-50 text-[#1a1a1a] text-[12px] leading-relaxed whitespace-pre-line">
            {text.bot}
          </div>
        </div>
      </div>
      {/* Source */}
      <div className="shrink-0 pt-2 border-t border-gray-100">
        <span className="text-[10px] text-gray-300">{text.source}</span>
      </div>
    </div>
  )
}

export function CapabilitiesSection() {
  const { ref, isVisible } = useScrollAnim()
  const { t } = useI18n()
  const [hoveredDemo, setHoveredDemo] = useState<number | null>(null)

  const featured = [
    {
      icon: Bot,
      title: t('企业专属AI管家', 'Dedicated AI Butler'),
      description: t('基于您的知识库量身定制，精准理解业务场景，每个回答都源于企业自身知识，而非通用模型猜测', 'Custom-built from your knowledge base, precisely understanding business scenarios with answers grounded in your own data'),
      accent: 'bg-gradient-to-br from-indigo-50 to-violet-50',
      iconBg: 'bg-indigo-500',
      stat: t('98%', '98%'),
      statLabel: t('准确率', 'Accuracy'),
      demoIdx: 0,
    },
    {
      icon: Database,
      title: t('智能知识库', 'Smart Knowledge Base'),
      description: t('一键上传文档即刻生效，产品手册、规章制度、操作规程秒级精准检索，支持PDF/Word/Excel', 'Upload documents and they\'re live instantly. Product manuals, policies, SOPs retrieved in seconds. PDF/Word/Excel supported'),
      accent: 'bg-gradient-to-br from-amber-50 to-orange-50',
      iconBg: 'bg-amber-500',
      stat: t('3s', '3s'),
      statLabel: t('检索速度', 'Retrieval'),
      demoIdx: 1,
    },
  ]

  const more = [
    { icon: Brain, title: t('深度推理引擎', 'Deep Reasoning Engine'), desc: t('DeepSeek R1推理模型，复杂问题条分缕析', 'DeepSeek R1 reasoning model for complex problem analysis') },
    { icon: Lock, title: t('数据安全隔离', 'Data Isolation'), desc: t('企业知识库独立存储，支持私有化部署', 'Isolated storage per enterprise, private deployment supported') },
    { icon: Cpu, title: t('多模型灵活切换', 'Multi-Model Switching'), desc: t('DeepSeek + Qwen双引擎智能路由', 'DeepSeek + Qwen dual-engine intelligent routing') },
    { icon: Puzzle, title: t('开放API无缝集成', 'Open API Integration'), desc: t('REST API与SSE流式接口对接企业系统', 'REST API & SSE streaming for enterprise system integration') },
    { icon: Zap, title: t('30分钟极速部署', '30-Min Deployment'), desc: t('上传→配置→上岗，三步完成上线', 'Upload → Configure → Deploy in three simple steps') },
    { icon: Shield, title: t('Token额度精细管控', 'Token Quota Control'), desc: t('按企业按用户分配，成本透明可追溯', 'Per-enterprise, per-user allocation with transparent cost tracking') },
  ]

  const highlights = [
    { icon: Sparkles, label: t('自动知识抽取', 'Auto Extraction'), desc: t('AI自动解析文档结构', 'AI parses document structure automatically') },
    { icon: Globe, label: t('企业官网展示', 'Website Showcase'), desc: t('浏览AI赋能的企业官网', 'Browse AI-powered enterprise websites') },
    { icon: BarChart3, label: t('数据洞察报表', 'Data Insights'), desc: t('对话数据可视化分析', 'Conversational data visualization') },
    { icon: Layers, label: t('多应用管理', 'Multi-App Mgmt'), desc: t('一个平台管理多个AI应用', 'Manage multiple AI apps on one platform') },
  ]

  return (
    <section id="capabilities" className="py-24 min-h-screen flex items-center" style={{ scrollSnapAlign: 'start' }}>
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-3 py-1 text-xs font-medium text-indigo-500 bg-indigo-50 rounded-full mb-4">
            {t('核心优势', 'Core Advantages')}
          </span>
          <h2 className="text-2xl md:text-4xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
            {t('为什么选择 PrimeBook AI', 'Why PrimeBook AI')}
          </h2>
          <p className="text-sm text-gray-400 max-w-md">
            {t('不只是聊天机器人，而是扎根企业知识、真正懂你业务的AI管家', 'Not just a chatbot — an AI Butler rooted in enterprise knowledge that truly understands your business')}
          </p>
        </div>

        {/* Featured 2-up with chat demos on hover */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          {featured.map((item, i) => {
            const isHovered = hoveredDemo === item.demoIdx
            return (
              <div
                key={item.title}
                className={`group relative rounded-3xl ${item.accent} border border-white/80 p-8 hover:shadow-xl hover:shadow-black/[0.04] hover:-translate-y-1.5 transition-all duration-500 cursor-pointer overflow-hidden ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: isVisible ? `${i * 100}ms` : '0ms' }}
                onMouseEnter={() => setHoveredDemo(item.demoIdx)}
                onMouseLeave={() => setHoveredDemo(null)}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className={`h-12 w-12 rounded-2xl ${item.iconBg} flex items-center justify-center shadow-sm`}>
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#1a1a1a]">{item.stat}</div>
                    <div className="text-[10px] text-gray-400">{item.statLabel}</div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>

                {/* Hover hint */}
                <div className={`flex items-center gap-1.5 mt-4 text-[11px] text-violet-400 transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-60'}`}>
                  <MessageCircle className="h-3 w-3" />
                  {t('悬停查看真实对话', 'Hover to see real chats')}
                </div>

                <ChatOverlay
                  demoIdx={item.demoIdx}
                  iconBg={item.iconBg}
                  icon={item.icon}
                  title={item.title}
                  isHovered={isHovered}
                />
              </div>
            )
          })}
        </div>

        {/* Highlight strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {highlights.map((item, i) => (
            <div
              key={item.label}
              className={`group flex items-center gap-3 rounded-2xl bg-white/60 border border-white/80 p-4 hover:bg-white hover:shadow-md transition-all duration-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: isVisible ? `${(i + 2) * 80}ms` : '0ms' }}
            >
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                <item.icon className="h-4 w-4 text-indigo-500" />
              </div>
              <div>
                <div className="text-xs font-semibold text-[#1a1a1a]">{item.label}</div>
                <div className="text-[10px] text-gray-400">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Compact grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {more.map((item, i) => (
            <div
              key={item.title}
              className={`group rounded-2xl bg-white/80 border border-black/[0.04] p-5 hover:bg-white hover:shadow-md hover:shadow-black/[0.04] transition-all duration-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: isVisible ? `${(i + 6) * 60}ms` : '0ms' }}
            >
              <item.icon className="h-5 w-5 text-gray-400 mb-3 group-hover:text-gray-700 transition-colors" />
              <h4 className="text-xs font-semibold text-[#1a1a1a] mb-1">{item.title}</h4>
              <p className="text-[11px] text-gray-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
